import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import Contact from '../components/Contact';
import BargeConfigurator from '../components/BargeConfigurator';
import SectionHead from '../components/SectionHead';
import { Toaster } from '../components/ui/toaster';
import useSeo from '../hooks/useSeo';

const TITLE = 'Build a Custom HDPE Work Barge Online | Absolute Barges';
const DESCRIPTION =
  "Configure a custom HDPE work barge online — pick your 8'x24' or 10'x30' hull, single or twin Suzuki 115 outboards, hydraulic crane, spuds system, captain cabin, pneumatic hammer and more. Built in Fort Myers, FL. Delivered nationwide.";

// HowTo JSON-LD — tells Google + AI assistants that /build is a step-by-step
// tool page (not a FAQ or product landing). Different @type = different SERP
// treatment and different downstream routing when LLMs answer buyer-intent
// queries like "how do I buy a work barge?".
const HOWTO_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Build a Custom HDPE Work Barge',
  description:
    'Configure your custom HDPE work barge online: pick a hull size, engine setup, and every option you want on board — then submit for a written quote within one business day.',
  totalTime: 'PT10M',
  estimatedCost: {
    '@type': 'MonetaryAmount',
    currency: 'USD',
    value: '47250',
  },
  supply: [
    { '@type': 'HowToSupply', name: 'HDPE Hull (8\u2032\u00d724\u2032 or 10\u2032\u00d730\u2032)' },
    { '@type': 'HowToSupply', name: 'Suzuki 115 Outboard(s)' },
    { '@type': 'HowToSupply', name: 'Hydraulic Crane, Spuds, Cabin, Hammer, Electrical (optional)' },
  ],
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Choose your hull size',
      text:
        "Pick the 8'x24' HDPE hull (15,000 lb payload, easy road transport, tight-canal maneuverability) or the 10'x30' (18,000 lb+ payload, wide-load permit required, tows smaller barges).",
      url: 'https://absolutebarges.com/build#hull',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Choose your engine',
      text:
        'Single Suzuki 115 for economical protected-water work, or twin Suzuki 115s for redundancy, higher speed under load, and stronger station-keeping when running a crane or hammer.',
      url: 'https://absolutebarges.com/build#engine',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Add your specific features',
      text:
        "Add any combination of Hydraulic Crane 25' (12,500 lb winch), Twin Spuds with Winch, Captain's Cabin, Rigging & Outboard Install, Premium Whole-Boat Electrical, 30-gal 175-PSI Air Compressor, Bilge Pump with Autostart, Solar Panel, Gauges & Chartplotter, ABSOLUTE Pneumatic Hammer 700, and a matched trailer.",
      url: 'https://absolutebarges.com/build#features',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Submit for a written quote',
      text:
        'Your full spec sheet is copied into our contact form. A team member replies with a written quote within one business day. Financing is available for Florida buyers. Delivery available to the Lower 48 with a live shipping estimator on the delivery page.',
      url: 'https://absolutebarges.com/build#send',
    },
  ],
};

// Readable spec prose reused from our internal quote templates. Renders
// as real text on the page so Google, Bing, and AI assistants can read
// and cite it. Also mirrored in <noscript> inside public/index.html so
// the initial HTML source contains meaningful content even without JS.
const SPECS = [
  {
    title: "Hull — 8' × 24' or 10' × 30'",
    body:
      "Corrosion-proof HDPE hull. The 8' × 24' rides 15,000 lb payload with excellent maneuverability for tight canals and residential waterways. The 10' × 30' rides 18,000+ lb, requires a wide-load permit, and can push or tow smaller barges.",
  },
  {
    title: 'Single or Twin Suzuki 115 Outboards',
    body:
      'Choose one 115 hp Suzuki for economical work in protected water, or twin 115s for redundancy, higher speed under load, and stronger station-keeping when running a crane or hammer. Custom motor packages are available on request.',
  },
  {
    title: "Hydraulic Crane 25'",
    body:
      "Hydraulic lift system with manual extension and rotation. 12,500 lb electric winch. Rated to lift pilings, dock hardware, and small dock frames. Painted crane arm can be color-matched to your company brand.",
  },
  {
    title: 'Spuds System with Winch',
    body:
      "Twin spuds with dedicated winch let you plant the barge onto the bottom for stable pile driving, dredging, and dock work in current or wake. Sized for typical southwest Florida bottom types.",
  },
  {
    title: "Captain's Cabin",
    body:
      "Enclosed helm option with hard top, windshield, and side panels — keeps your operator out of the sun, wind, and rain on long jobs. Open-helm build is also available.",
  },
  {
    title: 'Rigging & Outboard Motor Install',
    body:
      "Full rigging package: throttle, steering, gauges, wiring runs, and factory-approved outboard installation. Motors are hung, aligned, tested, and delivered ready to run.",
  },
  {
    title: 'Premium Whole-Boat Electrical',
    body:
      "Lithium-ion battery, solar panel with charge controller, LED work-lights and nav-lights, USB and 12 V outlets. All wiring runs are elevated inside the hull for saltwater durability, and every fitting is rated for the marine environment.",
  },
  {
    title: 'Air Compressor (30 gal, 175 PSI)',
    body:
      'Deck-mounted air compressor sized to drive our Absolute Pneumatic Hammer 700 all day. Includes hose reel and quick-connect fittings.',
  },
  {
    title: 'Bilge Pump with Autostart',
    body:
      "Automatic bilge pump kicks on when water gets into the hull. Combined with our sealed HDPE compartments, this gives you a redundant layer of protection against sinking.",
  },
  {
    title: 'Solar Panel',
    body:
      "Deck-mounted solar panel charges the lithium-ion house battery. The charge controller and smart battery disconnect automatically when full — no risk of overcharging even if you leave the barge on the water for weeks.",
  },
  {
    title: 'Extra Gauges & Chartplotter',
    body:
      "Add engine gauges, fuel flow, GPS chartplotter, and depth sounder to make the barge a full-fledged working platform rather than just a hull.",
  },
  {
    title: 'ABSOLUTE Pneumatic Hammer 700',
    body:
      "Our proprietary galvanized pneumatic pile-driving hammer. Uses air pressure to both LIFT and DROP the ram for maximum striking power. Off-hammer black-box controller. Ships with a 1-year electronics warranty. Sold standalone at $10,500 or bundled into your barge build.",
  },
  {
    title: 'Trailer',
    body:
      "Matched trailer sized for your hull. New or used, depending on availability. Trailers typically run $7,000–$15,000 depending on size and specifications.",
  },
];

export default function BuildMyBarge() {
  useSeo({
    title: TITLE,
    description: DESCRIPTION,
    canonical: 'https://absolutebarges.com/build',
    jsonLd: [{ id: 'howto-build-barge', data: HOWTO_JSONLD }],
  });

  // Auto-open the configurator modal on mount so /build is the tool,
  // not just a description page. Fired after paint so the surrounding
  // page (with its indexable prose) is rendered first.
  useEffect(() => {
    const t = setTimeout(() => {
      window.dispatchEvent(new Event('open-barge-configurator'));
    }, 250);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="App bg-black min-h-screen">
      <Navigation />
      <main>
        <section className="relative py-20 sm:py-24 lg:py-28 bg-black overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
          <div className="absolute -top-20 right-0 w-[480px] h-[480px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
            <SectionHead
              eyebrow="Configure Your Custom HDPE Work Barge Online"
              titleLines={['Build My Barge']}
              body="Design your ideal HDPE work barge in your browser: pick your hull size, engine configuration, crane, spuds, pneumatic hammer and every feature you want on board. When you're done, we copy your full spec sheet into our quote form so you get a written quote back within one business day."
            />

            <div className="mt-8">
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('open-barge-configurator'))}
                data-testid="build-open-configurator-btn"
                className="btn-teal px-6 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.18em]"
              >
                Open the configurator
              </button>
              <span className="ml-4 text-zinc-500 text-sm">
                or scroll down to read the full spec of every option.
              </span>
            </div>

            <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
              {SPECS.map((s) => (
                <article key={s.title} className="panel rounded-xl p-5 sm:p-6">
                  <h2 className="font-display text-white text-lg sm:text-xl font-semibold tracking-wide mb-2">
                    {s.title}
                  </h2>
                  <p className="text-zinc-400 text-sm leading-relaxed">{s.body}</p>
                </article>
              ))}
            </div>

            <div className="mt-14 rounded-xl border border-cyan-400/25 bg-cyan-500/[0.05] p-6 sm:p-7">
              <h3 className="font-display text-white text-lg sm:text-xl font-semibold mb-2">
                Financing & Delivery
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Financing is currently available for Florida buyers and we are actively signing more lenders. Delivery is available to the Lower 48 — a live shipping estimator is on the{' '}
                <Link to="/#coverage" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">delivery page</Link>{' '}
                so you can price freight before you request a full quote.
              </p>
            </div>
          </div>
        </section>

        <Contact />
      </main>
      <Footer />
      <BargeConfigurator />
      <Toaster />
    </div>
  );
}
